import { useState, useRef, useEffect, useCallback } from 'react';
import { ChatLinkText } from './ChatLinkText';
import axios from 'axios';
import { Send, Loader2, Sparkles, Headset } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = 'dyssc_chat_session';
const POLL_MS = 6000;

const WELCOME = {
  role: 'bot',
  text:
    "Hey! \uD83D\uDC4B Not sure which paddle suits you, or have a question about delivery or an order? Ask me, or tap one below.",
};

const QUICK_REPLIES = [
  { label: 'Shipping time', text: 'How long does shipping take?' },
  { label: 'Returns', text: 'What is your return policy?' },
  { label: 'Warranty', text: 'Do your paddles have a warranty?' },
  { label: 'Pricing', text: 'How much do your paddles cost?' },
  { label: 'Track my order', text: 'I want to track my order' },
];

/** Render *bold* + newlines safely (light theme). */
function FormattedText({ text }) {
  const lines = (text || '').split('\n');
  return (
    <>
      {lines.map((line, li) => {
        const parts = line.split(/(\*[^*]+\*)/g).filter(Boolean);
        return (
          <span key={li} className="block">
            {parts.map((p, pi) =>
              p.startsWith('*') && p.endsWith('*') && p.length > 2 ? (
                <strong key={pi} className="font-semibold text-[#111214]">{p.slice(1, -1)}</strong>
              ) : (
                <ChatLinkText key={pi} text={p} className="text-[#E02128] hover:text-[#111214]" />
              ),
            )}
          </span>
        );
      })}
    </>
  );
}

export function InlineConcierge() {
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [started, setStarted] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);
  const lastCountRef = useRef(0);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  }, []);
  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  // Restore prior session (shared with the floating widget) instantly.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let sid = null;
      try { sid = localStorage.getItem(SESSION_KEY); } catch (e) { /* ignore */ }
      if (!sid) return;
      sessionRef.current = sid;
      try {
        const { data: t } = await axios.get(`${API}/chat/thread`, { params: { session_id: sid } });
        if (!cancelled && (t.messages || []).length) {
          setMessages([WELCOME, ...t.messages]);
          lastCountRef.current = t.messages.length;
          setStarted(true);
        }
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const syncThread = useCallback(async () => {
    const sid = sessionRef.current;
    if (!sid) return;
    try {
      const { data } = await axios.get(`${API}/chat/thread`, { params: { session_id: sid } });
      const srv = data.messages || [];
      if (srv.length !== lastCountRef.current) {
        lastCountRef.current = srv.length;
        setMessages([WELCOME, ...srv]);
      }
    } catch (e) { /* ignore */ }
  }, []);

  // Poll for human-agent replies once a conversation exists.
  useEffect(() => {
    if (!started) return undefined;
    const id = setInterval(syncThread, POLL_MS);
    return () => clearInterval(id);
  }, [started, syncThread]);

  const sendText = useCallback(async (text) => {
    if (!text || busy) return;
    setStarted(true);
    setMessages((m) => [...m, { role: 'user', text }]);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/chat/message`, { session_id: sessionRef.current, text });
      if (data.session_id) {
        sessionRef.current = data.session_id;
        try { localStorage.setItem(SESSION_KEY, data.session_id); } catch (err) { /* ignore */ }
      }
      await syncThread();
      if (!data.reply && data.intent === 'human_takeover') {
        setMessages((m) => [...m, { role: 'note', text: 'Sent — a DYSSC team member will reply here shortly.' }]);
      }
    } catch {
      setMessages((m) => [
        ...m,
        { role: 'bot', text: "Sorry, I couldn't reach the server. Please try again or tap *Continue on WhatsApp*." },
      ]);
    } finally {
      setBusy(false);
    }
  }, [busy, syncThread]);

  const onSubmit = (e) => {
    e.preventDefault();
    const t = input.trim();
    if (!t) return;
    setInput('');
    sendText(t);
  };

  const continueOnWhatsApp = () => {
    window.open('https://wa.me/919962044222', '_blank', 'noopener');
  };

  return (
    <div
      className="bg-white border border-zinc-200 rounded-2xl shadow-sm h-[460px] flex flex-col overflow-hidden"
      data-testid="inline-concierge"
    >
      {/* Header */}
      <div className="bg-zinc-50 border-b border-zinc-200 px-4 py-3 flex justify-between items-center">
        <div className="flex items-center gap-2.5">
          <div className="relative">
            <div className="w-9 h-9 rounded-full bg-[#111214] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-zinc-50" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[#111214] leading-tight">DYSSC Support</p>
            <p className="text-[11px] text-emerald-600 font-medium">Online · replies instantly</p>
          </div>
        </div>
        <button
          onClick={continueOnWhatsApp}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-[#52525B] hover:text-[#111214] transition-colors"
          data-testid="concierge-whatsapp-btn"
        >
          <Headset className="w-4 h-4" /> Human
        </button>
      </div>

      {/* Chat area */}
      <div ref={scrollRef} className="flex-1 px-4 py-4 overflow-y-auto space-y-3 bg-white scrollbar-hide">
        {messages.map((m, i) => {
          if (m.role === 'note') {
            return (
              <p key={i} className="text-center text-[11px] text-[#A1A1AA] py-1">{m.text}</p>
            );
          }
          const mine = m.role === 'user';
          return (
            <div key={i} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div
                className={
                  mine
                    ? 'bg-[#111214] text-white rounded-2xl rounded-tr-sm px-3.5 py-2.5 max-w-[82%] text-sm leading-relaxed'
                    : 'bg-zinc-50 border border-zinc-100 text-[#27272A] rounded-2xl rounded-tl-sm px-3.5 py-2.5 max-w-[82%] text-sm leading-relaxed'
                }
              >
                <FormattedText text={m.text} />
              </div>
            </div>
          );
        })}
        {busy && (
          <div className="flex justify-start">
            <div className="bg-zinc-50 border border-zinc-100 rounded-2xl rounded-tl-sm px-3.5 py-3">
              <span className="flex gap-1">
                <span className="w-1.5 h-1.5 bg-zinc-300 rounded-full animate-bounce [animation-delay:-0.3s]" />
                <span className="w-1.5 h-1.5 bg-zinc-300 rounded-full animate-bounce [animation-delay:-0.15s]" />
                <span className="w-1.5 h-1.5 bg-zinc-300 rounded-full animate-bounce" />
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Quick replies */}
      <div className="px-3 pt-2 flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
        {QUICK_REPLIES.map((q) => (
          <button
            key={q.label}
            onClick={() => sendText(q.text)}
            disabled={busy}
            className="shrink-0 rounded-full border border-zinc-200 bg-white hover:bg-zinc-50 hover:border-zinc-300 text-xs font-medium text-zinc-700 px-3.5 py-1.5 transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E02128] focus-visible:ring-offset-1"
            data-testid={`quick-reply-${q.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {q.label}
          </button>
        ))}
      </div>

      {/* Input */}
      <form onSubmit={onSubmit} className="p-3 border-t border-zinc-200 bg-white flex items-center gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type your question…"
          className="flex-1 bg-zinc-50 border border-zinc-200 rounded-full px-4 py-2.5 text-sm text-[#111214] placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#E02128]/40 focus:border-zinc-300"
          data-testid="concierge-input"
        />
        <button
          type="submit"
          disabled={busy || !input.trim()}
          className="w-10 h-10 shrink-0 rounded-full bg-[#E02128] text-white flex items-center justify-center hover:bg-[#c01c22] transition-colors disabled:opacity-40"
          data-testid="concierge-send-btn"
          aria-label="Send message"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </div>
  );
}
